import { HiOutlineExclamation, HiOutlineTrash, HiOutlineX } from "react-icons/hi";

type ConfirmVariant = "default" | "danger";

type ConfirmDialogProps = {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmVariant;
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
};

export default function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  variant = "default",
  busy = false,
  onConfirm,
  onClose,
}: ConfirmDialogProps): JSX.Element | null {
  if (!open) return null;

  const isDanger = variant === "danger";
  const Icon = isDanger ? HiOutlineTrash : HiOutlineExclamation;
  const iconWrapClass = isDanger
    ? "flex size-8 items-center justify-center rounded-full bg-[#FEE2E2]"
    : "flex size-8 items-center justify-center rounded-full bg-[#FEF3C7]";
  const iconClass = isDanger ? "size-5 text-[#DC2626]" : "size-5 text-[#D97706]";
  const confirmBtnClass = isDanger
    ? "rounded-md bg-[#DC2626] px-4 py-2 text-xs font-semibold leading-5 text-white hover:bg-[#B91C1C] disabled:opacity-60 disabled:cursor-not-allowed"
    : "rounded-md bg-[#222] px-4 py-2 text-xs font-semibold leading-5 text-white hover:bg-[#333] disabled:opacity-60 disabled:cursor-not-allowed";

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center px-4"
      style={{ fontFamily: "Inter, sans-serif" }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
    >
      <div
        className="fixed inset-0 bg-[#222] opacity-60"
        aria-hidden
        onClick={busy ? undefined : onClose}
      />
      <div className="relative z-10 flex w-full max-w-[440px] flex-col gap-5 rounded-[10px] bg-white shadow-lg">
        <div className="flex shrink-0 items-center justify-between border-b border-solid border-[#e3e3e3] p-5">
          <div className="flex items-center gap-2">
            <span className={iconWrapClass}>
              <Icon className={iconClass} strokeWidth={2} />
            </span>
            <h2
              id="confirm-dialog-title"
              className="text-base font-semibold leading-5 text-[#222]"
            >
              {title}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="flex size-5 items-center justify-center rounded text-[#6d6d6d] hover:bg-[#eee] hover:text-[#222] disabled:opacity-50"
            aria-label="Close"
          >
            <HiOutlineX className="size-5" strokeWidth={2} />
          </button>
        </div>
        {description ? (
          <div className="px-5">
            <p className="text-sm leading-5 text-[#555]">{description}</p>
          </div>
        ) : null}
        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-solid border-[#eee] p-3">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-solid border-[#eee] bg-white px-4 py-2 text-xs font-semibold leading-5 text-[#6d6d6d] hover:bg-[#f5f5f5] disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button type="button" onClick={onConfirm} disabled={busy} className={confirmBtnClass}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
